// src/services/matrixService.js
// Matrix SDK service - centralized Matrix client management and room operations

import {
  createClient,
  ClientEvent,
  MatrixEventEvent,
  RoomMemberEvent,
  IndexedDBCryptoStore,
  Preset,
  Visibility,
} from 'matrix-js-sdk';
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api';
import recoveryEphemeralStore from './recoveryEphemeralStore';
import draftStorageService from './draftStorageService';
import logger from '../utils/logger';

/**
 * Matrix Service - Handles all Matrix SDK operations
 * Manages client lifecycle, room operations, and message handling
 * 
 * Note: This service no longer extends EventEmitter to avoid redundant events.
 * The Matrix SDK already provides all necessary events through the client.
 * Components should use hooks to subscribe to client events directly.
 */
class MatrixService {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.isSyncing = false;
    this.syncState = null;
    this.isSyncStarted = false; // Track if sync has been started to prevent double-starts

    // Temporary key for verification purposes only
    this._temporaryVerificationKey = null;

    // Timer for throttled room refresh after decryption
    this.refreshTimer = null;

    // Timer for sync state check
    this.syncStateCheckTimer = null;

    // Throttling for presence updates to prevent 429 rate limit errors
    this.lastPresenceUpdate = 0;
    this.minPresenceInterval = 30000; // 30 seconds minimum between updates (increased from 10s to avoid 429 errors)
    this.pendingPresenceTimer = null; // Timer for deferred presence updates
    this.presenceRetryCount = 0; // Track retry attempts for exponential backoff
    this.maxPresenceRetries = 3; // Maximum number of retries
    this.presenceRetryTimeoutId = null; // Timeout ID for presence retry (for cleanup)

    // Handler reference for page visibility API
    this._visibilityChangeHandler = null;

    // Presence configuration
    this.sendPresenceUpdates = true;
    // Logout prevention flag to avoid duplicate logout attempts
    this._loggingOut = false;

    // Pending DM rooms cache to prevent race condition during invite acceptance
    // Maps roomId → { timestamp, timerId } for recently accepted DM invites
    // This ensures isDMRoom() returns true during the gap between membership change
    // and m.direct account data update arriving from the server
    this.pendingDMRooms = new Map();

    // Periodic cleanup interval to prevent memory leaks (starts when client is initialized)
    this.pendingDMCleanupInterval = null; // Don't start yet
  }

  /**
   * Reset device keys on the server (use when getting key upload conflicts)
   */
  async resetDeviceKeys() {
    try {
      logger.debug('Attempting to reset device keys on server');

      // Get current credentials to make direct API calls
      const savedCreds = localStorage.getItem('matrixCreds');
      if (!savedCreds) {
        logger.warn('No credentials found, cannot reset device keys');
        return false;
      }

      const creds = JSON.parse(savedCreds);
      const deviceId = creds.deviceId;
      const accessToken = creds.accessToken;
      const baseUrl = creds.homeserverUrl;

      // First, try to delete existing one-time keys
      logger.debug('Attempting to clear one-time keys from server');

      // Upload empty one-time keys to clear them
      const keysUploadUrl = `${baseUrl}/_matrix/client/v3/keys/upload`;
      try {
        const emptyKeysResponse = await fetch(keysUploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            one_time_keys: {},  // Empty one-time keys to clear server state
          }),
        });

        if (emptyKeysResponse.ok) {
          const result = await emptyKeysResponse.json();
          logger.info('Cleared one-time keys from server:', result);
        }
      } catch (error) {
        logger.warn('Could not clear one-time keys:', error.message);
      }

      // Optionally delete the entire device (more drastic)
      if (localStorage.getItem('matrixForceDeviceReset') === 'true') {
        const deleteUrl = `${baseUrl}/_matrix/client/v3/devices/${deviceId}`;

        try {
          const response = await fetch(deleteUrl, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auth: {
                type: 'm.login.password',
                user: creds.userId,
                password: localStorage.getItem('matrixTempPassword') || '',
              },
            }),
          });

          if (response.ok) {
            logger.info('Device deleted from server successfully');
            localStorage.removeItem('matrixForceDeviceReset');
            localStorage.removeItem('matrixTempPassword');
            return true;
          } else {
            logger.warn('Could not delete device from server:', response.status, response.statusText);
          }
        } catch (fetchError) {
          logger.warn('Network error deleting device:', fetchError.message);
        }
      }

      return false;
    } catch (error) {
      logger.warn('Could not reset device keys:', error.message);
      return false;
    }
  }

  /**
   * Set temporary verification key for SSSS callback during verification
   * This key is used only during the verification process and is cleared afterward
   * @param {Uint8Array} keyBytes - The recovery key bytes to use temporarily
   */
  setTemporaryVerificationKey(keyBytes) {
    // Make a copy to avoid mutating the input
    this._temporaryVerificationKey = keyBytes ? new Uint8Array(keyBytes) : null;
  }

  /**
   * Clear the temporary verification key
   */
  clearTemporaryVerificationKey() {
    if (this._temporaryVerificationKey) {
      // Zero out the key bytes for security
      if (this._temporaryVerificationKey.fill) {
        this._temporaryVerificationKey.fill(0);
      }
      this._temporaryVerificationKey = null;
    }
  }

  /**
   * Clear ALL Matrix-related crypto stores (for dev/testing)
   * Now with proper transaction handling to avoid InvalidStateError
   */
  async clearAllMatrixStores() {
    try {
      // Stop the client first if it's running to close all connections
      if (this.client && this.isSyncing) {
        logger.debug('Stopping client before clearing stores');
        this.client.stopClient();
        this.isSyncing = false;
        // Wait for client to fully stop
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Get all databases
      const databases = await window.indexedDB.databases();

      // Find all Matrix-related databases
      const matrixDatabases = databases.filter(db =>
        db.name && db.name.includes('matrix-js-sdk'),
      );

      if (matrixDatabases.length === 0) {
        logger.debug('No Matrix databases found to clear');
        return;
      }

      logger.info('Found Matrix databases to clear:', matrixDatabases.map(db => db.name));

      // Delete each Matrix database with proper error handling
      const deletePromises = matrixDatabases.map(db => {
        return new Promise((resolve) => {
          try {
            const deleteReq = window.indexedDB.deleteDatabase(db.name);

            deleteReq.onsuccess = () => {
              logger.info(`Cleared database: ${db.name}`);
              resolve(true);
            };

            deleteReq.onerror = () => {
              // Don't throw on error, just log and continue
              logger.warn(`Could not clear database ${db.name}:`, deleteReq.error);
              resolve(false);
            };

            deleteReq.onblocked = () => {
              logger.warn(`Database ${db.name} deletion blocked - connections still open`);
              resolve(false);
            };
          } catch (e) {
            logger.warn(`Error initiating database deletion for ${db.name}:`, e);
            resolve(false);
          }
        });
      });

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r).length;
      logger.info(`Cleared ${successCount}/${matrixDatabases.length} Matrix crypto stores`);

    } catch (error) {
      logger.error('Error clearing Matrix stores:', error);
      // Don't throw - just log the error
    }
  }

  /**
   * Initialize Matrix client with stored credentials
   */
  async initialize() {
    if (this.isInitialized && this.client) {
      return this.client;
    }

    try {
      const savedCreds = localStorage.getItem('matrixCreds');
      if (!savedCreds) {
        throw new Error('No Matrix credentials found');
      }

      const creds = JSON.parse(savedCreds);
      await this.createClient(creds);
      
      this.isInitialized = true;
      logger.info('Matrix service initialized successfully');
      
      return this.client;
    } catch (error) {
      logger.error('Failed to initialize Matrix service:', error);
      throw error;
    }
  }

  /**
   * Get the current Matrix client instance
   * @returns {MatrixClient|null} The Matrix client or null if not initialized
   */
  getClient() {
    return this.client;
  }

  buildTIMUserAgent() {
    // Client identifier (can be configured via constants or env variables)
    const clientId = 'NetSfere TI-M WebClient';
    const clientVersion = '1.0.0';

    // Detect OS and version
    let osVersion = 'Unknown';
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent;

      // IMPORTANT: Order matters! Mobile devices often include desktop OS strings in their UA.
      // We check mobile platforms BEFORE desktop platforms to avoid false positives.
      //
      // Example: iPad on iOS 13+ reports as:
      // "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_6) AppleWebKit/605.1.15..."
      // This UA includes "Mac OS X" but is actually an iPad!
      // By checking for iPhone/iPad first, we correctly identify iOS devices.

      // iOS detection (must come before macOS check)
      if (ua.includes('iPhone') || ua.includes('iPad')) {
        const match = ua.match(/OS ([\d_]+)/);
        if (match) {
          osVersion = `iOS ${match[1].replace(/_/g, '.')}`;
        }
      }
      // Android detection (must come before Linux check since Android UA contains "Linux")
      else if (ua.includes('Android')) {
        const match = ua.match(/Android ([\d.]+)/);
        if (match) {
          osVersion = `Android ${match[1]}`;
        }
      }
      // macOS detection (comes after iOS to avoid iPad misidentification)
      else if (ua.includes('Mac OS X')) {
        const match = ua.match(/Mac OS X ([\d_]+)/);
        if (match) {
          osVersion = `macOS ${match[1].replace(/_/g, '.')}`;
        }
      }
      // Windows detection
      else if (ua.includes('Windows NT')) {
        const match = ua.match(/Windows NT ([\d.]+)/);
        if (match) {
          const ntVersion = match[1];
          const winVersionMap = {
            '10.0': 'Windows 10/11',
            '6.3': 'Windows 8.1',
            '6.2': 'Windows 8',
            '6.1': 'Windows 7',
          };
          osVersion = winVersionMap[ntVersion] || `Windows NT ${ntVersion}`;
        }
      }
      // Linux detection (comes last since Android also includes "Linux")
      else if (ua.includes('Linux')) {
        osVersion = 'Linux';
        // Try to detect specific distribution
        if (ua.includes('Ubuntu')) {
          osVersion = 'Ubuntu';
        } else if (ua.includes('Fedora')) {
          osVersion = 'Fedora';
        }
      }

    }

    // Format: "ClientID/Version, OS Version"
    return `${clientId}/${clientVersion}, ${osVersion}`;
  }

  // Build a custom fetch function
  customFetch = (url, options = {}) => {
    logger.debug('Custom fetch called for URL:', url);
    const headers = new Headers(options.headers || {});
    const timUserAgent = this.buildTIMUserAgent();
    headers.set('X-TIM-User-Agent', timUserAgent);
    //More headers (VZD - if needed can be added here if needed)
    return fetch(url, { ...options, headers });
  };

  /**
   * Create and configure Matrix client
   */
  async createClient(credentials) {
    logger.info('Creating Matrix client', {
      userId: credentials.userId,
      deviceId: credentials.deviceId,
      homeserver: credentials.homeserverUrl,
    });

    // Create a unique crypto store name to avoid conflicts
    const cryptoStoreName = `matrix-js-sdk:crypto:${credentials.userId}:${credentials.deviceId}`;
    logger.debug('Using crypto store name:', cryptoStoreName);

    // Check if we should force clear crypto stores (manual override)
    const forceClearStores = localStorage.getItem('matrixForceClearStores') === 'true';

    if (forceClearStores) {
      logger.info('Force clearing crypto stores (manual override set)');
      await this.clearAllMatrixStores();
      // Clear the flag after use
      localStorage.removeItem('matrixForceClearStores');
    }

    // Only try to reset device keys if explicitly needed
    const shouldResetDeviceKeys = localStorage.getItem('matrixResetDeviceKeys') === 'true';
    if (shouldResetDeviceKeys) {
      logger.info('Resetting device keys on server (manual override set)');
      await this.resetDeviceKeys();
      localStorage.removeItem('matrixResetDeviceKeys');
    }
    
    // For development: Option to disable crypto to avoid key upload issues
    const disableCrypto = process.env.NODE_ENV === 'development' && 
                         localStorage.getItem('matrixDisableCrypto') === 'true';
    
    if (disableCrypto) {
      logger.info('Crypto disabled for development (set matrixDisableCrypto=false in localStorage to re-enable)');
    }
    
    const opts = {
      baseUrl: credentials.homeserverUrl,
      accessToken: credentials.accessToken,
      userId: credentials.userId,
      deviceId: credentials.deviceId,
      idBaseUrl: credentials.identityServerUrl,
      // Only include crypto store if crypto is enabled
      ...(disableCrypto ? {} : {
        cryptoStore: new IndexedDBCryptoStore(
          window.indexedDB, 
          cryptoStoreName,
        ),
      }),
      fetchFn: this.customFetch,
      cryptoCallbacks: {
          getSecretStorageKey: async (opts, name) => {
          logger.debug('TI-M: getSecretStorageKey called');
          logger.debug('TI-M: opts parameter:', opts);
          logger.debug('TI-M: name parameter:', name);

          // Handle different parameter formats
          // The SDK may call with ({ keys: {...} }, name) or just (keys, undefined)
          let keyInfos = opts?.keys || opts;
          let keyId = null;

          // If opts has a keys property, use that
          if (opts && typeof opts === 'object' && opts.keys) {
            keyInfos = opts.keys;
          }

          // Try to determine the key ID to use
          // IMPORTANT: 'name' parameter is often a secret name (like m.cross_signing.master)
          // NOT a key ID. The actual key IDs are in the keyInfos object
          if (keyInfos && typeof keyInfos === 'object') {
            // Try to get the default key ID from keyInfos
            const keyIds = Object.keys(keyInfos);

            if (keyIds.length === 1) {
              // Only one key available, use it
              keyId = keyIds[0];
            } else {
              // Multiple keys, try to find default
              try {
                const defaultKeyData = await this.client?.getAccountData?.('m.secret_storage.default_key');
                const defaultKeyId = defaultKeyData?.getContent?.()?.key;
                if (defaultKeyId && keyInfos[defaultKeyId]) {
                  keyId = defaultKeyId;
                } else {
                  // Use first available key
                  keyId = keyIds[0];
                }
              } catch {
                // Fallback to first key
                keyId = keyIds[0];
              }
            }
          }

          if (!keyId) {
            logger.debug('TI-M: Could not determine key ID');
            return null;
          }

          // First check if we have a temporary verification key, otherwise use ephemeral store
          const userKeyBytes = this._temporaryVerificationKey || recoveryEphemeralStore.getKey();

          if (!userKeyBytes || userKeyBytes.length !== 32) {
            logger.debug('TI-M: No recovery key available - returning null');
            return null;
          }

          // CRITICAL: Return tuple [keyId, keyBytes] like Element Web does
          return [keyId, userKeyBytes];
        },
      },
    };

    this.client = createClient(opts);
    
    if (!disableCrypto) {
      logger.info('Matrix client created, initializing Rust crypto');
      
      try {
        await this.client.initRustCrypto();
        logger.info('Rust crypto initialized successfully');
      } catch (cryptoError) {
        logger.error('Crypto initialization failed:', cryptoError);

        // Only clear stores if it's a specific key conflict
        if (
          cryptoError.message.includes('already exists') ||
          cryptoError.message.includes('One time key') ||
          cryptoError.message.includes('crypto_store_already_open')
        ) {
          logger.info('Key conflict detected, will clear stores and retry');

          // If it's specifically a one-time key conflict, try to reset server keys first
          if (cryptoError.message.includes('One time key')) {
            logger.info('One-time key conflict detected, attempting to reset server keys');
            const resetSuccess = await this.resetDeviceKeys();

            if (resetSuccess) {
              // If device was fully reset, we need to re-login
              logger.warn('Device was reset on server, re-login required');
              throw new Error('DEVICE_RESET_REQUIRED');
            }
          }

          // Wait a bit to ensure any pending transactions complete
          await new Promise(resolve => setTimeout(resolve, 100));

          // Clear the conflicting stores
          await this.clearAllMatrixStores();

          // Wait a bit more after clearing
          await new Promise(resolve => setTimeout(resolve, 100));

          // Recreate client with fresh crypto store
          this.client = createClient(opts);

          // Try one more time
          try {
            await this.client.initRustCrypto();
            logger.info('Rust crypto initialized successfully after store clear');
          } catch (retryError) {
            logger.error('Failed to initialize crypto after retry:', retryError);
            // Continue without crypto rather than failing completely
            logger.warn('Continuing without encryption support');
            this.client = createClient({ ...opts, cryptoStore: undefined });
          }
        } else {
          // For other errors, don't clear stores - just throw
          throw cryptoError;
        }
      }
    } else {
      logger.info('Skipping crypto initialization (disabled for development)');
    }
    
    // Setup key backup after crypto is initialized
    logger.debug('TI-M: About to call setupKeyBackup()');
    await this.setupKeyBackup();
    logger.debug('TI-M: setupKeyBackup() returned');

    logger.debug('Setting up event handlers');
    this.setupEventHandlers();
    logger.debug('Event handlers setup completed');
    
    // Note: setSyncPresence is no longer needed since we disabled presence in sync
    // Presence is now managed by Page Visibility API
    
    // Setup Page Visibility API for presence management
    this.setupPageVisibilityHandling();
    
    logger.debug('Matrix client created with options:', opts);
    return this.client;
  }

  /**
   * Setup key backup and restore encrypted messages
   */
  async setupKeyBackup() {
    logger.debug('TI-M: setupKeyBackup() called');
    try {
      const crypto = this.client.getCrypto();
      logger.debug('TI-M: Got crypto instance:', !!crypto);
      if (!crypto) {
        logger.debug('TI-M: Crypto not available - exiting');
        return;
      }

      // Use the correct Rust crypto API to check for existing backup
      logger.debug('TI-M: Calling getKeyBackupInfo()...');
      const backupInfo = await crypto.getKeyBackupInfo();
      logger.debug('TI-M: backupInfo result:', backupInfo);
      if (!backupInfo) {
        logger.info('No key backup found on server');
        logger.debug('TI-M: No backup found, trying checkKeyBackupAndEnable()');
        // Try to check and enable backup anyway
        const backupCheck = await crypto.checkKeyBackupAndEnable();
        logger.debug('TI-M: checkKeyBackupAndEnable result:', backupCheck);
        if (backupCheck) {
          logger.info('Key backup enabled after check:', backupCheck);
        }
        return;
      }

      logger.debug('TI-M: Key backup found! Version:', backupInfo.version);
      
      // Check if backup is already trusted
      logger.debug('TI-M: Checking if backup is trusted...');
      const trustInfo = await crypto.isKeyBackupTrusted(backupInfo);
      logger.info('Key backup trust info:', trustInfo);
      logger.debug('TI-M: Trust info:', trustInfo);
      
      // Log more details about the backup
      logger.debug('TI-M: Backup auth_data:', backupInfo.auth_data);
      logger.debug('TI-M: Current device ID:', this.client.getDeviceId());
      logger.debug('TI-M: Current user ID:', this.client.getUserId());
      
      // Check device verification status
      const deviceId = this.client.getDeviceId();
      const userId = this.client.getUserId();
      logger.debug('TI-M: Checking device verification status...');
      const device = await crypto.getUserDeviceInfo([userId]);
      const myDevice = device.get(userId)?.get(deviceId);
      logger.debug('TI-M: My device verified status:', myDevice?.verified);
      logger.debug('TI-M: My device trust level:', myDevice?.trustLevel);
      
      // Check cross-signing status
      const crossSigningStatus = await crypto.getCrossSigningStatus();
      logger.debug('TI-M: Cross-signing status:', crossSigningStatus);
      
      if (!trustInfo.trusted) {
        logger.debug('TI-M: Backup NOT trusted, attempting to restore...');
        
        // Try to bootstrap cross-signing if not set up
        if (!crossSigningStatus.privateKeysInSecretStorage || !crossSigningStatus.publicKeysOnDevice) {
          logger.debug('TI-M: Cross-signing not fully set up, attempting to bootstrap...');
          try {
            logger.debug('TI-M: Bootstrapping cross-signing with recovery key...');
            await crypto.bootstrapCrossSigning({
              authUploadDeviceSigningKeys: async (makeRequest) => {
                logger.debug('TI-M: Uploading device signing keys...');
                await makeRequest({});
              },
            });
            logger.debug('TI-M: ✅ CROSS-SIGNING BOOTSTRAPPED SUCCESSFULLY');
            
            // Mark our own device as verified
            logger.debug('TI-M: MARKING OWN DEVICE AS VERIFIED...');
            await crypto.setDeviceVerified(userId, deviceId);
            logger.debug('TI-M: ✅ DEVICE MARKED AS VERIFIED');
          } catch (bootstrapErr) {
            logger.debug('TI-M: Failed to bootstrap cross-signing:', bootstrapErr.message);
          }
        } else {
          // Cross-signing is set up, but we still need to verify the device
          logger.debug('TI-M: Cross-signing already set up, checking device verification...');
          const device = await crypto.getUserDeviceInfo([userId]);
          const myDevice = device.get(userId)?.get(deviceId);
          
          if (myDevice?.verified !== 1 && myDevice?.verified !== true) {
            logger.debug('TI-M: Device not verified despite cross-signing being set up');
            logger.debug('TI-M: ATTEMPTING TO VERIFY DEVICE...');
            try {
              await crypto.setDeviceVerified(userId, deviceId);
              logger.debug('TI-M: ✅ DEVICE HAS BEEN VERIFIED SUCCESSFULLY');
            } catch (verifyErr) {
              logger.debug('TI-M: Failed to verify device:', verifyErr.message);
            }
          } else {
            logger.debug('TI-M: Device already verified (status:', myDevice?.verified, ')');
            // Even if device shows as verified, try to re-verify it
            logger.debug('TI-M: RE-VERIFYING DEVICE TO ENSURE BACKUP TRUST...');
            try {
              await crypto.setDeviceVerified(userId, deviceId);
              logger.debug('TI-M: ✅ DEVICE RE-VERIFIED SUCCESSFULLY');
            } catch (verifyErr) {
              logger.debug('TI-M: Re-verification error (may be already verified):', verifyErr.message);
            }
          }
        }
        
        try {
          // First check if we already have a stored backup key
          logger.debug('TI-M: Checking for stored backup key...');
          const storedKey = await crypto.getSessionBackupPrivateKey();
          logger.debug('TI-M: Stored key exists:', !!storedKey);

          // Only attempt automatic key injection if explicitly enabled
          const { isEnabled } = await import('../config/featureFlags');
          const autoRecoveryEnabled = isEnabled('autoRecovery');

          if (!trustInfo.trusted && autoRecoveryEnabled) {
            logger.debug('TI-M: AutoRecovery enabled — waiting for user-provided recovery key');
            // AutoRecovery feature flag is enabled but requires user to provide their own recovery key
            // This flag only controls whether the recovery flow automatically starts, not key injection
          } else if (!trustInfo.trusted) {
            logger.debug('TI-M: AutoRecovery disabled — waiting for user-provided key');
          }
          
          // Try loading from secret storage as an alternative approach
          try {
            logger.debug('TI-M: Attempting to load backup key from secret storage...');
            await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            logger.debug('TI-M: Successfully loaded from secret storage');
          } catch (secretErr) {
            logger.debug('TI-M: Could not load from secret storage:', secretErr.message);
          }
          
          // Check if device is now verified
          const deviceAfterSetup = await crypto.getUserDeviceInfo([userId]);
          const myDeviceAfter = deviceAfterSetup.get(userId)?.get(deviceId);
          logger.debug('TI-M: Device verified after setup:', myDeviceAfter?.verified);
          
          // Force a re-check to enable backup now that we have the key
          logger.debug('TI-M: Rechecking backup trust after storing key...');
          const recheckResult = await crypto.checkKeyBackupAndEnable();
          logger.debug('TI-M: Recheck result:', recheckResult);
          if (recheckResult && recheckResult.trustInfo && recheckResult.trustInfo.trusted) {
            logger.debug('TI-M: ✅ BACKUP IS NOW TRUSTED! STARTING RESTORATION...');
            
            // Start restoring from backup
            logger.info('Starting key backup restoration...');
            logger.debug('TI-M: CALLING RESTOREKEYBACKUP()...');
            await crypto.restoreKeyBackup();
            logger.info('Key backup restoration completed');
            logger.debug('TI-M: 🎉 KEY BACKUP RESTORATION COMPLETED SUCCESSFULLY!');
          } else {
            logger.debug('TI-M: Backup still NOT trusted after providing key');
            logger.debug('TI-M: Trust info details:', recheckResult?.trustInfo);
            
            // Try one more approach - attempt to restore anyway even if not trusted
            logger.debug('TI-M: Attempting to verify key matches backup...');
            const currentStoredKey = await crypto.getSessionBackupPrivateKey();
            if (currentStoredKey) {
              logger.debug('TI-M: Key is stored, but backup not trusting it');
              logger.debug('TI-M: Attempting to restore anyway with untrusted backup...');
              
              try {
                // Try to restore even though it's not trusted
                // This might work if the key is correct but trust verification has issues
                logger.debug('TI-M: Force attempting restoreKeyBackup despite trust issues...');
                await crypto.restoreKeyBackup();
                logger.debug('TI-M: 🎉 RESTORE SUCCEEDED DESPITE TRUST ISSUES!');
              } catch (restoreErr) {
                logger.debug('TI-M: Force restore failed:', restoreErr.message);
                
                // Try using the active session backup version
                const activeVersion = await crypto.getActiveSessionBackupVersion();
                logger.debug('TI-M: Active backup version:', activeVersion);
                
                if (!activeVersion && backupInfo.version) {
                  logger.debug('TI-M: No active backup. The recovery key does not match the server backup.');
                  logger.debug('TI-M: Options:');
                  logger.debug('TI-M: 1. Get the correct recovery key for this backup');
                  logger.debug('TI-M: 2. Delete this backup and create a new one (will lose old messages)');
                  logger.debug('TI-M: 3. Continue without backup (messages will decrypt as keys arrive)');
                  
                  // For now, continue without backup restoration
                  logger.debug('TI-M: Continuing without backup restoration');
                  logger.debug('TI-M: Messages will decrypt as keys are shared from other devices');
                }
              }
            }
          }
        } catch (restoreError) {
          logger.error('Failed to restore key backup:', restoreError);
          logger.debug('TI-M: ERROR restoring backup:', restoreError.message);
        }
      } else {
        logger.info('Key backup is already trusted');
        logger.debug('TI-M: Backup is ALREADY TRUSTED');
        
        // Ensure backup is enabled
        logger.debug('TI-M: Checking active backup version...');
        const activeVersion = await crypto.getActiveSessionBackupVersion();
        logger.debug('TI-M: Active version:', activeVersion, 'Current version:', backupInfo.version);
        if (activeVersion !== backupInfo.version) {
          logger.debug('TI-M: Versions differ, enabling backup...');
          const enableResult = await crypto.checkKeyBackupAndEnable();
          logger.info('Key backup enabled:', enableResult);
          logger.debug('TI-M: Backup enabled:', enableResult);
        } else {
          logger.info('Key backup already active for version:', activeVersion);
          logger.debug('TI-M: Backup already active');
        }
        
        // Restore keys from backup if not already done
        try {
          logger.info('Restoring keys from trusted backup...');
          logger.debug('TI-M: Restoring keys from trusted backup...');
          await crypto.restoreKeyBackup();
          logger.info('Key restoration completed');
          logger.debug('TI-M: 🎉 KEY RESTORATION COMPLETED!');
        } catch (restoreErr) {
          logger.warn('Key restoration error (may be already complete):', restoreErr.message);
          logger.debug('TI-M: Restore error (may be already complete):', restoreErr.message);
        }
      }
      
    } catch (error) {
      logger.error('Failed to setup key backup:', error);
      logger.debug('TI-M: CRITICAL ERROR in setupKeyBackup:', error.message, error.stack);
      // Don't throw - allow app to continue even if backup fails
    }
    logger.debug('TI-M: setupKeyBackup() finished');
  }

  /**
   * Set up event handlers for the Matrix client
   */
  setupEventHandlers() {
    if (!this.client) return;

    logger.debug('Setting up Matrix event handlers');

    // Setup session invalidation detection
    this.setupSessionInvalidationHandler();

    // Sync state handler
    this.client.on(ClientEvent.Sync, (state, prevState, data) => {
      // DEBUG: Inspect sync response for account_data
      if (data?.accountData) {
        logger.debug('[MatrixService] Sync response contains account_data:', data.accountData.length, 'events');
        data.accountData.forEach(event => {
          logger.debug('[MatrixService] Account data event type:', event.type);
          if (event.type === 'm.direct') {
            logger.debug('[MatrixService] m.direct found in sync response! Content:', JSON.stringify(event.content));
          }
        });
      } else {
        logger.debug('[MatrixService] Sync response has NO account_data field');
      }

      this.syncState = state;
      logger.debug('Sync state change:', {
        newState: state,
        prevState: prevState,
        isSyncing: this.isSyncing,
      });

      if (state === 'PREPARED') {
        this.isSyncing = true;
        logger.info('Matrix sync prepared - real-time events active');

        logger.debug('Matrix sync prepared - real-time events active');

        this.onSyncPrepared();
      } else if (state === 'SYNCING') {
        logger.debug('Matrix client is syncing');
        this.isSyncing = true;
      } else if (state === 'CATCHUP') {
        logger.debug('Matrix client catching up on missed events');
      } else if (state === 'ERROR') {
        this.isSyncing = false;
        logger.error('Matrix sync error:', data);

        // Handle crypto store errors specifically
        if (data?.error?.message?.includes('crypto store') ||
            data?.error?.message?.includes('InvalidStateError') ||
            data?.error?.message?.includes('IDBDatabase')) {
          logger.error('Crypto store error detected during sync:', data.error);
          // Log for debugging - hooks will handle state updates from client events
        }
      } else if (state === 'STOPPED') {
        this.isSyncing = false;
        this.isSyncStarted = false; // Reset on stop
        logger.info('Matrix sync stopped');
      } else if (state === 'RECONNECTING') {
        logger.debug('Matrix sync reconnecting');
      }

      // Sync state is now handled by hooks listening to ClientEvent.Sync directly
    });

    // Room events - log only for debugging
    this.client.on(ClientEvent.Room, (room) => {
      logger.debug('Room event:', room.roomId, room.name);
      // Hooks will handle room updates by listening to this event directly
    });

    // Timeline events are handled by hooks directly

    // Handle crypto errors to prevent key upload conflicts
    logger.debug('Setting up crypto error handlers');
    this.client.on('crypto.keyBackupFailed', (error) => {
      logger.warn('Key backup failed:', error.message);
      // Don't throw - just log and continue
    });
    
    this.client.on('crypto.keyBackupRestoreFailed', (error) => {
      logger.warn('Key backup restore failed:', error.message);
      // Don't throw - just log and continue
    });

    // Setup verification request listener
    this.setupVerificationListener();

    // Listen for m.direct account data updates to clear cache when confirmed
    this.client.on(ClientEvent.AccountData, (event) => {
      if (event.getType() === 'm.direct') {
        const content = event.getContent();
        const confirmedRoomIds = new Set();

        // Collect all room IDs from m.direct
        Object.values(content).forEach(roomList => {
          if (Array.isArray(roomList)) {
            roomList.forEach(roomId => confirmedRoomIds.add(roomId));
          }
        });

        // Remove from pending cache if now confirmed in m.direct
        for (const [roomId, metadata] of this.pendingDMRooms.entries()) {
          if (confirmedRoomIds.has(roomId)) {
            // Clear the timer
            if (metadata.timerId) {
              clearTimeout(metadata.timerId);
            }
            this.pendingDMRooms.delete(roomId);
            logger.debug(`[matrixService] Removed ${roomId} from pending cache (confirmed in m.direct event)`);
          }
        }
      }
    });

    // Handle HTTP errors that might include key upload failures
    this.client.on('httpError', (error, method, url) => {
      if (url.includes('/keys/upload') && error.httpStatus === 400) {
        logger.warn('Key upload failed (400 Bad Request) - duplicate keys on server:', error.message);

        // Check if it's the one-time key conflict
        if (error.message?.includes('One time key') || error.message?.includes('already exists')) {
          logger.error('One-time key conflict detected. Server has these keys already.');

          // Set a flag for user to manually reset if needed
          localStorage.setItem('matrixKeyConflictDetected', 'true');

          // Log the conflict - components can check localStorage flag if needed
          logger.error('Key upload conflict:', {
            error: error.message,
            suggestion: 'Try logging out and back in, or reset device keys',
          });
        }
        // Don't throw - Matrix SDK will handle retries
      } else if (url.includes('/sync')) {
        logger.error('Sync HTTP Error:', {
          method,
          url,
          status: error.httpStatus,
          message: error.message,
        });
      } else if (error.httpStatus >= 400) {
        logger.error('HTTP Error:', {
          method,
          url,
          status: error.httpStatus,
          message: error.message,
        });
      }
    });

    // Real-time message handling - use generic event listener since RoomTimeline doesn't work reliably
    this.client.on('event', (event) => {
      const eventType = event.getType();
      const roomId = event.getRoomId();
      const sender = event.getSender();
      const currentUserId = this.client.getUserId();
      
      // Log important events for debugging
      if (roomId && ['m.room.message', 'm.room.encrypted', 'm.room.redaction', 'm.room.member'].includes(eventType)) {
        logger.debug('Matrix event received:', {
          type: eventType,
          roomId: roomId,
          sender: sender,
        });
      }
      
      // Handle room messages for real-time updates
      if (eventType === 'm.room.message' && roomId) {
        logger.debug('Real-time message received:', {
          eventType,
          roomId,
          sender,
          isFromCurrentUser: sender === currentUserId,
        });
        
        // Hooks will handle message updates by listening to client events directly
      }
      
      // Handle encrypted messages
      if (eventType === 'm.room.encrypted' && roomId) {
        logger.debug('Real-time encrypted message received:', {
          eventType,
          roomId,
          sender,
        });
        
        // Hooks will handle encrypted message updates by listening to client events
      }
      
      // Handle message redactions (deletions)
      if (eventType === 'm.room.redaction' && roomId) {
        // Get redacted event ID from event content or properties
        let redactsEventId = null;
        try {
          // Try different ways to get the redacted event ID
          redactsEventId = event.redacts || 
                          event.event?.redacts || 
                          event.getContent()?.redacts ||
                          (event.event && event.event.redacts);
        } catch (e) {
          logger.debug('Could not get redacts ID:', e);
        }
        
        logger.debug('Real-time message redaction received:', {
          eventType,
          roomId,
          redactsEventId,
        });
        
        // Hooks will handle redaction updates by listening to client events
      }
      
      // Handle message edits/replacements
      if (eventType === 'm.room.message' && roomId) {
        const content = event.getContent();
        const isEdit = content['m.relates_to']?.rel_type === 'm.replace';
        
        if (isEdit) {
          const originalEventId = content['m.relates_to']?.event_id;
          
          logger.debug('Real-time message edit received:', {
            eventType,
            roomId,
            sender,
            originalEventId,
            newContent: content['m.new_content']?.body,
          });
          
          // Hooks will handle message edit updates by listening to client events
        }
      }
      
      // Handle read receipts  
      if (eventType === 'm.receipt' && roomId) {
        const receipts = event.getContent();
        
        logger.debug('Real-time read receipt received:', {
          eventType,
          roomId,
          receipts,
        });
        
        // Hooks will handle read receipt updates
      }
      
      // Handle room membership changes (joins, leaves, kicks, bans)
      if (eventType === 'm.room.member' && roomId) {
        const membership = event.getContent()?.membership;
        const targetUserId = event.getStateKey();

        logger.debug('Real-time membership change received:', {
          eventType,
          roomId,
          sender,
          targetUserId,
          membership,
        });

        // Hooks will handle membership updates
      }
      
      // Handle room avatar changes
      if (eventType === 'm.room.avatar' && roomId) {
        const newAvatar = event.getContent()?.url;
        
        logger.debug('Real-time room avatar change received:', {
          eventType,
          roomId,
          sender,
          newAvatar,
        });
        
        // Hooks will handle room avatar updates
      }
    });

    // Room state events (includes room deletion, kicks, bans, etc.)
    this.client.on(ClientEvent.RoomStateEvent, (event, state) => {
      logger.debug('Room state event:', event.getType(), 'in room:', state.roomId);
      
      // Handle events that might affect room membership or existence
      if (event.getType() === 'm.room.member' || 
          event.getType() === 'm.room.tombstone' ||
          event.getType() === 'm.room.create') {
        logger.debug('Room membership/existence changed');
        // Hooks will detect room state changes
      }
    });

    // Room member events (kicks, bans, leaves)
    this.client.on(ClientEvent.RoomMember, (event, state, member) => {
      // Check if this affects the current user
      const currentUserId = this.client.getUserId();
      if (member.userId === currentUserId) {
        logger.debug('Current user membership changed:', member.membership, 'in room:', member.roomId);
        // Hooks will detect membership changes
      }
    });

    // Redaction events (message deletions) - more specific handler
    this.client.on(ClientEvent.RoomRedaction, (event, room) => {
      logger.debug('Message redaction event:', {
        eventId: event.getId(),
        redactsEventId: event.getRedacts(),
        sender: event.getSender(),
        roomId: room.roomId,
      });
      
      // Hooks will handle redaction events
    });

    // Room receipt events (read receipts)
    this.client.on(ClientEvent.RoomReceipt, (event, room) => {
      logger.debug('Read receipt event in room:', room.roomId);
      // Hooks will handle read receipt events
    });

    // Message decryption events - refresh UI when messages are decrypted
    // This is crucial after recovery key restoration to show decrypted messages
    this.client.on(MatrixEventEvent.Decrypted, (event, err) => {
      if (err) {
        logger.debug('Message decryption failed:', event.getId(), err.message);
        return;
      }

      logger.debug('Message decrypted successfully:', event.getId());
      // Hooks will detect decrypted messages
    });
  }

  /**
   * Setup Page Visibility API to manage presence based on tab visibility
   * Sets presence to 'online' when tab is visible, 'unavailable' when hidden
   */
  setupPageVisibilityHandling() {
    if (typeof document === 'undefined' || !document.addEventListener) {
      logger.warn('Page Visibility API not available (SSR or old browser)');
      return;
    }

    // Remove existing listener before adding new one to prevent duplicates
    // This is critical when createClient() is called multiple times (e.g., during crypto retries)
    if (this._visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
      this._visibilityChangeHandler = null;
      logger.debug('Removed existing visibility change listener before re-registering');
    }

    // Handler for visibility changes
    const handleVisibilityChange = async () => {
      if (!this.client || !this.sendPresenceUpdates) return;

      const newPresence = document.hidden ? 'unavailable' : 'online';
      const userId = this.client.getUserId();

      // Throttle check to prevent 429 rate limit errors
      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastPresenceUpdate;
      
      if (timeSinceLastUpdate < this.minPresenceInterval) {
        const timeUntilNext = this.minPresenceInterval - timeSinceLastUpdate;
        
        // Clear any existing pending timer
        if (this.pendingPresenceTimer) {
          clearTimeout(this.pendingPresenceTimer);
        }
        
        // Schedule deferred update
        this.pendingPresenceTimer = setTimeout(() => {
          this.client.setPresence({
            presence: newPresence,
            status_msg: '',
          }).then(() => {
            this.lastPresenceUpdate = Date.now();
            logger.info(`Deferred presence update to ${newPresence} for ${userId}`);
            this.pendingPresenceTimer = null;
          }).catch(error => {
            logger.error('PRESENCE: Deferred update failed', error);
            logger.warn('Deferred presence update failed:', error.message);
            this.pendingPresenceTimer = null;
          });
        }, timeUntilNext);
        
        return; // Skip immediate update
      }

      // Clear any pending timer since we're doing immediate update
      if (this.pendingPresenceTimer) {
        clearTimeout(this.pendingPresenceTimer);
        this.pendingPresenceTimer = null;
      }
      
      
      try {
        // Set presence on server
        await this.client.setPresence({
          presence: newPresence,
          status_msg: '',
        });
        
        this.presenceRetryCount = 0; // Reset retry count on success
        this.lastPresenceUpdate = Date.now(); // Record successful update
        logger.info(`Tab ${document.hidden ? 'hidden' : 'visible'} - set presence to ${newPresence} for ${userId}`);
        
        // Note: The Matrix SDK will emit presence events automatically via sync
        // No need to manually emit events - this was causing timing issues where
        // events were emitted before PresenceManager subscribed
      } catch (error) {
        // Handle 429 rate limit errors with exponential backoff
        if (error.errcode === 'M_LIMIT_EXCEEDED' || error.httpStatus === 429) {
          this.handlePresenceRateLimit(error, newPresence);
        } else {
          logger.error('PRESENCE: Error setting presence', error);
          logger.warn('Failed to update presence on visibility change:', error.message);
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Defer initial presence update to avoid race conditions and 429 errors
    // Wait for sync to start before setting presence
    if (!document.hidden && this.sendPresenceUpdates) {
      const userId = this.client.getUserId();
      logger.debug('Scheduling initial presence update after sync starts');
      
      // Wait for sync to start (5 seconds delay to ensure client is fully ready)
      setTimeout(() => {
        // Double-check we're still visible and still want to send presence
        if (document.hidden || !this.sendPresenceUpdates || !this.client) {
          logger.debug('Skipping deferred initial presence - conditions changed');
          return;
        }
        
        // Throttle check to prevent 429 rate limit errors
        const now = Date.now();
        if (now - this.lastPresenceUpdate < this.minPresenceInterval) {
          logger.debug('Skipping deferred initial presence - too soon after last update');
          return;
        }
        
        // Set timestamp BEFORE async call to prevent race condition
        this.lastPresenceUpdate = now;
        
        this.client.setPresence({
          presence: 'online',
          status_msg: '',
        }).then(() => {
          this.presenceRetryCount = 0; // Reset retry count on success
          logger.info(`Set initial presence to online (tab is visible) for ${userId}`);
        }).catch(error => {
          // Handle 429 rate limit errors with exponential backoff
          if (error.errcode === 'M_LIMIT_EXCEEDED' || error.httpStatus === 429) {
            this.handlePresenceRateLimit(error, 'online');
          } else {
            // On other failures, reset timestamp to allow retry
            this.lastPresenceUpdate = 0;
            logger.error('PRESENCE: Failed to set initial presence', error);
            logger.warn('Failed to set initial presence:', error.message);
          }
        });
      }, 5000); // 5 second delay for initial presence
    }

    // Store reference for cleanup
    this._visibilityChangeHandler = handleVisibilityChange;
  }

  /**
   * Handle rate limit errors for presence updates with exponential backoff
   * @param {Error} error - The error from the presence update
   * @param {string} presence - The presence status to set ('online' or 'unavailable')
   */
  handlePresenceRateLimit(error, presence) {
    if (this.presenceRetryCount >= this.maxPresenceRetries) {
      logger.warn(`Presence update failed after ${this.maxPresenceRetries} retries, giving up`);
      this.presenceRetryCount = 0; // Reset for next attempt
      this.lastPresenceUpdate = 0; // Allow future updates
      return;
    }

    this.presenceRetryCount++;
    // Exponential backoff: 60s, 120s, 240s
    const backoffDelay = 60000 * Math.pow(2, this.presenceRetryCount - 1);
    
    logger.warn(`Presence rate limited (429), retrying in ${backoffDelay / 1000}s (attempt ${this.presenceRetryCount}/${this.maxPresenceRetries})`);

    this.presenceRetryTimeoutId = setTimeout(() => {
      this.presenceRetryTimeoutId = null;

      if (!this.client || !this.sendPresenceUpdates) {
        logger.debug('Skipping presence retry - client or presence disabled');
        return;
      }

      this.client.setPresence({
        presence: presence,
        status_msg: '',
      }).then(() => {
        this.presenceRetryCount = 0; // Reset on success
        this.lastPresenceUpdate = Date.now();
        logger.info(`Presence retry successful: ${presence}`);
      }).catch(retryError => {
        if (retryError.errcode === 'M_LIMIT_EXCEEDED' || retryError.httpStatus === 429) {
          // Recursive retry with backoff
          this.handlePresenceRateLimit(retryError, presence);
        } else {
          logger.error('Presence retry failed with non-rate-limit error:', retryError);
          this.presenceRetryCount = 0;
          this.lastPresenceUpdate = 0;
        }
      });
    }, backoffDelay);
  }

  /**
   * Clean up presence timer
   * Called when logging out or destroying the client
   */
  cleanupPresenceTimer() {
    if (this.pendingPresenceTimer) {
      clearTimeout(this.pendingPresenceTimer);
      this.pendingPresenceTimer = null;
    }
    if (this.presenceRetryTimeoutId) {
      clearTimeout(this.presenceRetryTimeoutId);
      this.presenceRetryTimeoutId = null;
    }
  }

  /**
   * Start the Matrix client sync
   */
  async startSync(options = {}) {
    if (!this.client) {
      await this.initialize();
    }

    // Prevent double-starting sync
    if (this.isSyncStarted) {
      logger.info('Matrix sync already started, skipping duplicate start');
      return;
    }

    // Check if client is already running
    if (this.client.clientRunning) {
      logger.info('Matrix client already running, skipping sync start');
      this.isSyncStarted = true;
      return;
    }

    const syncOptions = {
      initialSyncLimit: 10,
      pollTimeout: 30000, // 30 second long polling
      disablePresence: true, // Disable presence in sync - handled by Page Visibility API instead
      lazyLoadMembers: true, // Optimize loading
      // Force more aggressive syncing
      threadPoolSize: 3,
      includeArchivedRooms: false,
      ...options,
    };

    try {
      logger.info('Starting Matrix sync');
      this.isSyncStarted = true;
      this.client.startClient(syncOptions);

      // Start periodic cleanup for pending DM cache (only once)
      if (!this.pendingDMCleanupInterval) {
        this.pendingDMCleanupInterval = setInterval(() => {
          this.cleanupPendingDMCache();
        }, 3600000); // 1 hour
        logger.debug('[matrixService] Started pending DM cleanup interval (1 hour)');
      }

      // Note: Presence is now handled by Page Visibility API in setupPageVisibilityHandling()
      // This ensures presence is only 'online' when tab is visible, not 24/7

      // Monitor sync state for debugging
      this.syncStateCheckTimer = setTimeout(() => {
        // Check if client still exists (may have been cleared during logout)
        if (!this.client) {
          logger.debug('Sync state check skipped - client is null');
          return;
        }

        const syncState = this.client.getSyncState();
        logger.debug('Sync state check:', {
          syncState: syncState,
          isReady: this.isReady(),
          isSyncing: this.isSyncing,
          isSyncStarted: this.isSyncStarted,
        });
      }, 5000);
    } catch (error) {
      logger.error('Failed to start Matrix sync:', error);
      this.isSyncStarted = false; // Reset flag on error
      throw error;
    }
  }

  /**
   * Stop the Matrix client
   */
  async stopSync() {
    if (this.client) {
      logger.debug('Stopping Matrix client');
      this.client.stopClient();
      this.isSyncing = false;
      this.isSyncStarted = false; // Reset sync started flag
    }
  }

  setSendPresenceUpdates(value) {
    this.sendPresenceUpdates = value;
  }

  /**
   * Set user presence (Undefined presenceType will reset forcing to offline)
   */
  async setUserPresence(presenceType, statusMsg) {
    if (!this.client || !this.sendPresenceUpdates) return;
    
    try {
      await this.client.setPresence({
        presence: presenceType,
        status_msg: statusMsg,
      });
      logger.debug(`Updated presence to: ${presenceType}`);
    } catch (error) {
      logger.warn('Failed to update presence:', error.message);
    }
  }

  /**
   * Cleanup method to be called on logout
   * Ensures complete cleanup of Matrix client and its resources
   */
  async cleanup() {
    try {
      // Clean up presence timer first
      this.cleanupPresenceTimer();

      // Set presence to offline before cleanup
      if (this.client && this.sendPresenceUpdates) {
        try {
          await this.client.setPresence({
            presence: 'offline',
            status_msg: '',
          });
          logger.info('Set presence to offline on cleanup');
        } catch (error) {
          logger.warn('Failed to set offline presence:', error.message);
        }
      }

      // Remove visibility change listener
      if (this._visibilityChangeHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
        this._visibilityChangeHandler = null;
        logger.debug('Removed visibility change listener');
      }

      // Clear any pending timers first to prevent them from accessing null client
      if (this.syncStateCheckTimer) {
        clearTimeout(this.syncStateCheckTimer);
        this.syncStateCheckTimer = null;
        logger.debug('Cleared sync state check timer');
      }

      // Clear pending DM cleanup interval
      if (this.pendingDMCleanupInterval) {
        clearInterval(this.pendingDMCleanupInterval);
        this.pendingDMCleanupInterval = null;
        logger.debug('[matrixService] Cleared pending DM cleanup interval');
      }

      // Clear all pending DM timers and cache
      for (const [roomId, metadata] of this.pendingDMRooms.entries()) {
        if (metadata && metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
      }
      this.pendingDMRooms.clear();
      logger.debug('[matrixService] Cleared pending DM cache');

      // Stop sync if running
      if (this.client) {
        logger.info('Stopping Matrix sync for cleanup...');
        this.client.stopClient();
        
        // Wait for sync and crypto operations to fully complete shutdown
        // This prevents race conditions with crypto resources during logout
        logger.debug('Waiting for sync and crypto to complete shutdown...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        this.isSyncing = false;
        this.isSyncStarted = false;

        // Remove all event listeners
        this.client.removeAllListeners();

        // Clear client reference
        this.client = null;
      }

      // Reset initialization flag
      this.isInitialized = false;

      logger.info('Matrix service cleanup completed');
    } catch (error) {
      logger.error('Error during Matrix service cleanup:', error);
    }
  }

  /**
   * Called when sync is prepared - load initial data
   */
  onSyncPrepared() {
    const rooms = this.getRooms();
    logger.info(`Matrix sync prepared. Found ${rooms.length} rooms`);

    const dmRooms = this.client.getAccountData('m.direct')?.getContent() || {};

    
    // Log room information for debugging
    rooms.forEach(room => {
      logger.debug('Room:', {
        id: room.roomId,
        name: room.name || 'Unnamed Room',
        memberCount: room.getJoinedMemberCount(),
        unread: room.getUnreadNotificationCount(),
        isOneToOne: Object.values(dmRooms).some(dmList => dmList.includes(room.roomId)),
        roomType: room.getJoinRule(),
      });
    });

    // Hooks will handle initial room loading
  }

  /**
   * Get all rooms (conversations)
   */
  getRooms() {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return [];
    }
    return this.client.getRooms();
  }

  /**
   * Get display name for a user ID
   * @param {string} userId - The Matrix user ID (@user:server.com)
   * @param {string} roomId - Optional room ID for room-specific display name
   * @returns {Promise<string>} - The display name or user ID as fallback
   */
  async getUserDisplayName(userId, roomId = null) {
    if (!this.client) {
      return userId;
    }

    try {
      // Try to get room member first (room-specific display name)
      if (roomId) {
        const room = this.client.getRoom(roomId);
        if (room) {
          const member = room.getMember(userId);
          if (member && member.name) {
            return member.name;
          }
        }
      }

      // Fall back to global profile
      const profile = await this.client.getProfileInfo(userId);
      return profile.displayname || userId;
    } catch (error) {
      logger.debug('MatrixService: Could not get display name for user:', userId, error);
      return userId; // Fallback to user ID
    }
  }

  /**
   * Delete/redact a message from a room
   * In Matrix, this is called "redaction" - the message is replaced with a redacted placeholder
   * 
   * @param {string} roomId - The room ID containing the message
   * @param {string} eventId - The event ID of the message to delete
   * @param {string} reason - Optional reason for the deletion
   * @returns {Promise<Object>} - The redaction event response
   */
  async deleteMessage(roomId, eventId, reason = '') {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      logger.debug('MatrixService: Deleting message:', { roomId, eventId, reason });
      
      const content = reason ? { reason } : {};
      const response = await this.client.redactEvent(roomId, eventId, undefined, content);
      
      logger.debug('MatrixService: Message deleted successfully:', response);
      return response;
    } catch (error) {
      logger.error('MatrixService: Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Check if the current user can delete a specific message
   * Users can only delete their own messages, unless they have moderator permissions
   * 
   * @param {string} roomId - The room ID containing the message
   * @param {string} eventId - The event ID of the message to check
   * @returns {Promise<boolean>} - Whether the user can delete this message
   */
  async canDeleteMessage(roomId, eventId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const room = this.client.getRoom(roomId);
      if (!room) {
        logger.warn('MatrixService: Room not found for delete permission check:', roomId);
        return false;
      }

      // Get the event (message) to check
      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('MatrixService: Event not found for delete permission check:', eventId);
        return false;
      }

      const currentUserId = this.client.getUserId();

      // Check if user is the sender of the message
      const isOwnMessage = event.getSender() === currentUserId;

      // Check if user has permission to redact events in this room
      let canRedact = false;

      if (room.currentState) {
        const powerLevels = room.currentState.getStateEvents('m.room.power_levels', '');

        if (powerLevels) {
          const powerLevelContent = powerLevels.getContent();
          const userPowerLevel = powerLevelContent.users?.[currentUserId] || powerLevelContent.users_default || 0;
          const redactPowerLevel = powerLevelContent.redact || 50;

          canRedact = userPowerLevel >= redactPowerLevel;
        }
      }

      // User can delete if it's their own message OR they have redact permissions
      const canDelete = isOwnMessage || canRedact;
      
      logger.debug('MatrixService: Delete permission check:', {
        roomId,
        eventId,
        currentUserId,
        isOwnMessage,
        canRedact,
        canDelete,
      });

      return canDelete;
    } catch (error) {
      logger.error('MatrixService: Error checking delete permissions:', error);
      return false;
    }
  }

  /**
   * Get message details needed for deletion UI
   * Returns information about who sent the message and when
   * 
   * @param {string} roomId - The room ID containing the message
   * @param {string} eventId - The event ID of the message
   * @returns {Promise<Object|null>} - Message details or null if not found
   */
  async getMessageDetails(roomId, eventId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const room = this.client.getRoom(roomId);
      if (!room) {
        logger.warn('MatrixService: Room not found for message details:', roomId);
        return null;
      }

      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('MatrixService: Event not found for message details:', eventId);
        return null;
      }

      const sender = event.getSender();
      const timestamp = event.getTs();
      const content = event.getContent();
      const eventType = event.getType();

      return {
        eventId,
        sender,
        timestamp,
        content,
        eventType,
        isMessage: eventType === 'm.room.message',
        messageBody: content.body || '',
        messageType: content.msgtype || '',
      };
    } catch (error) {
      logger.error('MatrixService: Error getting message details:', error);
      return null;
    }
  }

  /**
   * Create a new room
   */
  async createRoom(options = {}) {
    logger.debug('[matrixService] createRoom - START with options:', JSON.stringify(options, null, 2));
    if (!this.client) {
      logger.error('[matrixService] ERROR - Matrix client not initialized in matrixService.createRoom');
      throw new Error('Matrix client not initialized');
    }

    try {
      const roomOptions = {
        visibility: Visibility.Private,
        preset: Preset.PrivateChat,
        ...options,
      };

      // Enable encryption by default for all new rooms
      if (!roomOptions.initial_state) {
        roomOptions.initial_state = [];
      }

      // Add encryption state event if not already present
      const hasEncryption = roomOptions.initial_state.some(
        state => state.type === 'm.room.encryption',
      );

      if (!hasEncryption && roomOptions.preset !== Preset.PublicChat) {
        roomOptions.initial_state.push({
          type: 'm.room.encryption',
          state_key: '',
          content: {
            algorithm: 'm.megolm.v1.aes-sha2',
          },
        });
        logger.debug('Encryption enabled for new room');
      } else {
        logger.debug('Not enabling encryption for new public room');
      }

      logger.debug('[matrixService] About to call this.client.createRoom with merged roomOptions:', JSON.stringify(roomOptions, null, 2));
      const response = await this.client.createRoom(roomOptions);
      logger.debug('[matrixService] this.client.createRoom SUCCESS - response:', JSON.stringify(response));
      logger.debug('Room created with id:', response);
      return response;
    } catch (error) {
      logger.error('[matrixService] ERROR in matrixService.createRoom:', error.message, error);
      logger.error('Error creating room:', error);
      throw error;
    }
  }

  /**
   * Join a room
   */
  async joinRoom(roomIdOrAlias) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const response = await this.client.joinRoom(roomIdOrAlias);
      logger.debug('Joined room:', response);
      return response;
    } catch (error) {
      logger.error('Error joining room:', error);
      throw error;
    }
  }

  /**
   * Leave a room
   */
  async leaveRoom(roomId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const response = await this.client.leave(roomId);
      logger.debug('Left room:', response);
      
      // Clear draft when leaving room
      draftStorageService.clearDraft(roomId);
      logger.debug('Cleared draft for room:', roomId);
      
      return response;
    } catch (error) {
      logger.error('Error leaving room:', error);
      throw error;
    }
  }

  /**
   * Forget a room (removes it from room list after leaving)
   */
  async forgetRoom(roomId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const response = await this.client.forget(roomId);
      logger.debug('Forgot room:', response);
      return response;
    } catch (error) {
      logger.error('Error forgetting room:', error);
      throw error;
    }
  }

  /**
   * Leave and forget a room (complete removal)
   */
  async leaveAndForgetRoom(roomId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      logger.debug('Leaving and forgetting room:', roomId);
      
      // First leave the room - handle if already left
      try {
        await this.leaveRoom(roomId);
      } catch (leaveError) {
        logger.debug('Room might already be left, continuing with forget:', leaveError.message);
        // Continue to forget even if leave fails (room might already be left)
      }
      
      // Then forget it to remove from room list - this is the key operation
      await this.forgetRoom(roomId);
      
      logger.debug('Successfully left and forgot room:', roomId);
      
      // Room destruction is handled - hooks will detect room removal from getRooms()
      
      return true;
    } catch (error) {
      logger.error('Error leaving and forgetting room:', error);
      throw error;
    }
  }

  /**
   * Find existing DM room with a specific user
   * @param {string} userId - User ID to search for
   * @returns {Room|null} Existing DM room or null if not found
   */
  findDMForUser(userId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return null;
    }

    try {
      const myUserId = this.client.getUserId();
      const directEvent = this.client.getAccountData('m.direct');
      const directContent = directEvent?.getContent() || {};
      const roomIds = directContent[userId] || [];

      // Find first active DM room with this user that meets all criteria:
      // 1. Current user is joined
      // 2. Target user is joined (not left/banned)
      // 3. Only 2 joined members (current user + target user)
      for (const roomId of roomIds) {
        const room = this.client.getRoom(roomId);
        if (!room) continue;

        // Check if current user is joined
        if (room.getMyMembership() !== 'join') {
          logger.debug(`Skipping room ${roomId}: current user not joined`);
          continue;
        }

        // Get all joined and invited members
        const allMembers = room.currentState.getMembers().filter(m => 
          m.membership === 'join' || m.membership === 'invite',
        );
        
        // Check if target user is still in the room (joined or invited)
        const targetMember = allMembers.find(m => m.userId === userId);
        if (!targetMember) {
          logger.debug(`Skipping room ${roomId}: target user ${userId} has left`);
          continue;
        }

        // Check if room has exactly 2 members (1-on-1 DM)
        if (allMembers.length !== 2) {
          logger.debug(`Skipping room ${roomId}: has ${allMembers.length} members, not a 1-on-1 DM`);
          continue;
        }

        // Verify the two members are current user and target user
        const memberIds = allMembers.map(m => m.userId).sort();
        const expectedIds = [myUserId, userId].sort();
        if (JSON.stringify(memberIds) !== JSON.stringify(expectedIds)) {
          logger.debug(`Skipping room ${roomId}: members don't match expected pair`);
          continue;
        }

        logger.debug(`Found valid existing 1-on-1 DM with ${userId}: ${roomId}`);
        return room;
      }

      logger.debug(`No valid 1-on-1 DM found with ${userId}`);
      return null;
    } catch (error) {
      logger.error('Error finding DM for user:', error);
      return null;
    }
  }

  /**
   * Set or unset a room as a direct message room
   * IMPORTANT: This is REQUIRED for proper DM functionality. The is_direct flag
   * on createRoom() does NOT automatically update m.direct account data.
   * 
   * Based on Element Web's implementation (matrix-react-sdk/src/Rooms.ts)
   * 
   * @param {string} roomId - The room ID to mark/unmark as DM
   * @param {string|null} userId - User ID to mark as DM partner, or null to unmark
   * @returns {Promise<void>}
   */
  async setDMRoom(roomId, userId) {
    logger.debug('[matrixService] setDMRoom - START - roomId:', roomId, 'userId:', userId);
    logger.debug('[matrixService] setDMRoom START - roomId:', roomId, 'userId:', userId);
    if (!this.client) {
      logger.error('[matrixService] ERROR - Matrix client not initialized in setDMRoom');
      throw new Error('Matrix client not initialized');
    }

    // Don't set DMs for guest users
    if (this.client.isGuest()) {
      logger.debug('[matrixService] Skipping DM room marking - user is guest');
      logger.debug('Skipping DM room marking for guest user');
      return;
    }

    // Capture timestamp once for consistent TTL tracking
    const originalTimestamp = Date.now();

    // Cancel existing timer if present (handle multiple calls for same room)
    if (this.pendingDMRooms.has(roomId)) {
      const existing = this.pendingDMRooms.get(roomId);
      if (existing && existing.timerId) {
        clearTimeout(existing.timerId);
        logger.debug(`[matrixService] setDMRoom: Cancelled existing timer for ${roomId}`);
      }
    }

    // Only add to pending cache if we're MARKING as DM (userId not null)
    // When unmarking (userId === null), we don't need to bridge any gap
    let successTimerId = null;
    if (userId) {
      // Create the timer FIRST, before adding to cache
      // This ensures the timer ID is available if AccountData event fires during server call
      successTimerId = setTimeout(() => {
        this.pendingDMRooms.delete(roomId);
        logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (60s timeout)`);
      }, 60000); // 60 seconds minimum TTL

      // Add to pending cache with timer ID BEFORE server update
      // This prevents race condition with AccountData event
      this.pendingDMRooms.set(roomId, { timestamp: originalTimestamp, timerId: successTimerId });
      logger.debug(`[matrixService] setDMRoom: Added ${roomId} to pending DM cache with 60s timer`);
    } else {
      // Unmarking - remove from pending cache if present
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] setDMRoom: Removing ${roomId} from pending cache (unmarking DM)`);
    }

    try {
      // Get current m.direct content
      logger.debug('[matrixService] Getting current m.direct account data');
      const directEvent = this.client.getAccountData('m.direct');
      const currentContent = directEvent?.getContent() || {};
      logger.debug('[matrixService] Current m.direct content:', JSON.stringify(currentContent, null, 2));

      const dmRoomMap = new Map(Object.entries(currentContent));
      let modified = false;

      // Remove this room from ALL users' DM lists
      // (a room can only be a DM with one person)
      logger.debug('[matrixService] Cleaning up room from other users DM lists (if present)');
      for (const [existingUserId, roomIds] of dmRoomMap.entries()) {
        if (existingUserId !== userId) {
          const indexOfRoom = roomIds.indexOf(roomId);
          if (indexOfRoom > -1) {
            logger.debug('[matrixService] Found room in user', existingUserId, 'DM list, removing');
            // Create new array without mutation
            const newRoomIds = roomIds.filter(id => id !== roomId);

            if (newRoomIds.length > 0) {
              dmRoomMap.set(existingUserId, newRoomIds);
            } else {
              // Clean up empty arrays to match Element Web behavior
              dmRoomMap.delete(existingUserId);
            }

            modified = true;
            logger.debug(`Removed room ${roomId} from ${existingUserId}'s DM list`);
          }
        }
      }

      // Add room to the target user's DM list
      if (userId) {
        logger.debug('[matrixService] Adding room to target user DM list - userId:', userId);
        const currentRoomList = dmRoomMap.get(userId) || [];
        logger.debug('[matrixService] Current room list for user', userId, ':', currentRoomList);
        if (!currentRoomList.includes(roomId)) {
          // Create NEW array instead of mutating - prevents SDK state corruption
          const newRoomList = [...currentRoomList, roomId];
          dmRoomMap.set(userId, newRoomList);
          modified = true;
          logger.debug('[matrixService] Room added to user DM list, new list:', newRoomList);
          logger.debug(`Added room ${roomId} to ${userId}'s DM list`);
        } else {
          logger.debug('[matrixService] Room already in user DM list');
        }
      }

      // Only call setAccountData if something actually changed
      if (!modified) {
        logger.debug('[matrixService] No changes to m.direct needed, skipping setAccountData');
        logger.debug('No changes needed to m.direct account data');
        return;
      }

      // Persist changes to account data
      const newMDirectContent = Object.fromEntries(dmRoomMap);
      logger.debug('[matrixService] About to call client.setAccountData with NEW m.direct content:', JSON.stringify(newMDirectContent, null, 2));
      logger.debug('[matrixService] Calling client.setAccountData for m.direct in matrixService.setDMRoom');

      await this.client.setAccountData('m.direct', newMDirectContent);
      logger.debug('[matrixService] client.setAccountData SUCCESS - server persisted the update');
      logger.info(`Successfully updated m.direct account data for room ${roomId} with user ${userId}`);

      // Success! Timer is already created and stored, nothing more to do

    } catch (error) {
      // Only handle timer cleanup if we created one (marking as DM)
      if (userId && successTimerId) {
        // Timer already created - clear it
        clearTimeout(successTimerId);

        // Distinguish between network errors and other errors
        const isNetworkError = error.name === 'ConnectionError' ||
                               error.message?.includes('network') ||
                               error.message?.includes('timeout') ||
                               error.message?.includes('ECONNREFUSED') ||
                               error.message?.includes('ETIMEDOUT');

        if (isNetworkError) {
          // Network error - keep in cache longer to allow retry/reconnection
          logger.debug(`[matrixService] setDMRoom: Network error, keeping ${roomId} in cache for recovery`);
          const networkTimerId = setTimeout(() => {
            this.pendingDMRooms.delete(roomId);
            logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (network recovery timeout)`);
          }, 120000); // 2 minutes for network recovery

          this.pendingDMRooms.set(roomId, { timestamp: originalTimestamp, timerId: networkTimerId });
        } else {
          // Other errors (permissions, validation, etc.) - remove from cache
          this.pendingDMRooms.delete(roomId);
          logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (error)`);
        }
      }

      logger.error('[matrixService] ERROR in setDMRoom:', error.message, error);
      logger.error('Error setting DM room:', error);
      throw error;
    }
  }

  /**
   * Clean up expired entries from pending DM cache
   * Called periodically (every 1 hour) to prevent memory leaks
   * @private
   */
  cleanupPendingDMCache() {
    const now = Date.now();
    const MAX_AGE = 3600000; // 1 hour maximum cache lifetime
    const MAX_CACHE_SIZE = 1000; // Maximum number of entries
    const expiredRooms = [];

    // Remove entries older than 1 hour
    for (const [roomId, metadata] of this.pendingDMRooms.entries()) {
      // Defensive check for corrupted entries
      if (!metadata || typeof metadata.timestamp !== 'number') {
        // Corrupted entry - clean up timer if exists and remove
        if (metadata && metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        this.pendingDMRooms.delete(roomId);
        logger.warn(`[matrixService] Cleaned up corrupted pending DM entry: ${roomId}`);
        continue;
      }

      if (now - metadata.timestamp > MAX_AGE) {
        // Clear timer if it exists
        if (metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        expiredRooms.push(roomId);
      }
    }

    expiredRooms.forEach(roomId => {
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] Cleaned up expired pending DM (1 hour): ${roomId}`);
    });

    // Enforce max cache size (remove oldest if over limit)
    let sizeRemovedCount = 0;
    if (this.pendingDMRooms.size > MAX_CACHE_SIZE) {
      const entries = Array.from(this.pendingDMRooms.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toRemove = entries.slice(0, this.pendingDMRooms.size - MAX_CACHE_SIZE);
      toRemove.forEach(([roomId, metadata]) => {
        if (metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        this.pendingDMRooms.delete(roomId);
        logger.debug(`[matrixService] Cleaned up pending DM (max size): ${roomId}`);
      });
      sizeRemovedCount = toRemove.length;
    }

    const totalCleaned = expiredRooms.length + sizeRemovedCount;
    logger.debug(`[matrixService] cleanupPendingDMCache: Removed ${totalCleaned} entries, ${this.pendingDMRooms.size} remaining`);

    return totalCleaned;
  }

  /**
   * Check if a room is marked as a direct message (DM) room
   *
   * This function checks TWO sources to handle both joined rooms and invites:
   * 1. m.direct account data (for joined/created DMs)
   * 2. is_direct flag in member event (for DM invites not yet accepted)
   *
   * This dual check ensures DM invites appear in the Conversations page
   * even before the invitee accepts and updates their m.direct.
   *
   * @param {string} roomId - The room ID to check
   * @returns {boolean} True if the room is a DM (in m.direct OR has is_direct flag), false otherwise
   */
  isDMRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return false;
    }

    try {
      // Check 1: m.direct account data (for joined DMs)
      const directEvent = this.client.getAccountData('m.direct');
      const directContent = directEvent?.getContent() || {};

      const inMDirect = Object.values(directContent).some((roomIds) =>
        Array.isArray(roomIds) && roomIds.includes(roomId),
      );

      if (inMDirect) {
        return true;
      }

      // Check 2: Pending DM rooms (recently accepted invites during m.direct update gap)
      // This prevents rooms from disappearing during the race condition window
      // Cleanup is handled by periodic audit (every 1 hour)
      if (this.pendingDMRooms.has(roomId)) {
        logger.debug(`[matrixService] isDMRoom: ${roomId} found in pending DM cache`);
        return true;
      }

      // Check 3: is_direct flag in member event (for DM invites not yet accepted)
      // This handles the case where User A creates a DM with User B:
      // - User A's m.direct is updated immediately (handled above)
      // - User B receives an invite with is_direct=true but m.direct NOT updated yet
      // - We check the is_direct flag so User B sees it in Conversations, not Channels
      const room = this.client.getRoom(roomId);
      if (!room) {
        return false;
      }

      const myUserId = this.client.getUserId();
      if (!myUserId) {
        return false;
      }

      const membership = room.getMyMembership();

      // Only check is_direct flag for invites
      // For joined rooms, m.direct should already be updated (via auto-accept)
      if (membership === 'invite' && room.currentState) {
        const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);
        if (memberEvent) {
          const isDirect = memberEvent.getContent().is_direct === true;
          if (isDirect) {
            logger.debug(`isDMRoom: Room ${roomId} is a DM invite (is_direct=true)`);
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      logger.error('Error checking if room is DM:', error);
      return false;
    }
  }

  /**
   * Get the OTHER user ID in a DM room from m.direct account data.
   * This is more reliable than using getJoinedMembers() during membership transitions.
   * 
   * @param {string} roomId - The room ID to look up
   * @returns {string|null} - The other user's ID, or null if not found or not a DM
   */
  getDMUserForRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return null;
    }

    try {
      const dmRooms = this.client.getAccountData('m.direct')?.getContent() || {};

      // Iterate through { userId: [roomIds] } structure
      for (const [userId, roomIds] of Object.entries(dmRooms)) {
        if (Array.isArray(roomIds) && roomIds.includes(roomId)) {
          return userId;
        }
      }

      return null;
    } catch (error) {
      logger.error('Error getting DM user for room:', error);
      return null;
    }
  }

  /**
   * Check if a room is a valid 1-to-1 DM room
   * A valid DM must be in m.direct AND have exactly 2 members
   * This is useful for filtering out "DM rooms" that have had additional members added
   *
   * @param {string} roomId - The room ID to check
   * @returns {boolean} True if the room is a proper 1-to-1 DM (in m.direct with 2 members)
   */
  isValidDMRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return false;
    }

    try {
      // First check if it's in m.direct
      const inMDirect = this.isDMRoom(roomId);
      if (!inMDirect) {
        return false;
      }

      // Then check if it has exactly 2 members
      const room = this.client.getRoom(roomId);
      if (!room) {
        return false;
      }

      const memberCount = room.getJoinedMemberCount();
      return memberCount === 2;
    } catch (error) {
      logger.error('Error checking if room is valid DM:', error);
      return false;
    }
  }

  /**
   * Get current user ID
   */
  getUserId() {
    return this.client?.getUserId() || null;
  }

  /**
   * Get client sync state
   */
  getSyncState() {
    return this.syncState;
  }

  /**
   * Check if client is ready
   */
  isReady() {
    return this.isInitialized && this.isSyncing && this.syncState === 'PREPARED';
  }

  /**
   * Event listener management
   */
  onSync(listener) {
    logger.debug(`MatrixService: Registering sync listener (total: ${this.syncListeners.length + 1})`);
    this.syncListeners.push(listener);
    return () => {
      const index = this.syncListeners.indexOf(listener);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  onRoom(listener) {
    this.roomListeners.push(listener);
    return () => {
      const index = this.roomListeners.indexOf(listener);
      if (index > -1) {
        this.roomListeners.splice(index, 1);
      }
    };
  }

  onMessage(listener) {
    this.messageListeners.push(listener);
    return () => {
      const index = this.messageListeners.indexOf(listener);
      if (index > -1) {
        this.messageListeners.splice(index, 1);
      }
    };
  }

  onReceipt(listener) {
    this.receiptListeners.push(listener);
    return () => {
      const index = this.receiptListeners.indexOf(listener);
      if (index > -1) {
        this.receiptListeners.splice(index, 1);
      }
    };
  }

  onMembership(listener) {
    this.membershipListeners.push(listener);
    return () => {
      const index = this.membershipListeners.indexOf(listener);
      if (index > -1) {
        this.membershipListeners.splice(index, 1);
      }
    };
  }

  /**
   * Add typing listener using proper RoomMemberEvent.Typing pattern
   * @param {string} roomId - The room ID to listen for typing events
   * @param {Function} callback - Callback function (event, member) => void
   * @returns {Function} Cleanup function to remove the listener
   */
  addTypingListener(roomId, callback) {
    if (!this.client) {
      logger.warn('MatrixService: Cannot add typing listener, client not available');
      return () => {}; // Return no-op cleanup function
    }

    // Create a wrapper that filters for the specific room
    const wrappedCallback = (event, member) => {
      // Only process events for our target room
      if (!member || member.roomId !== roomId) return;
      callback(event, member);
    };

    // Use client-level RoomMemberEvent.Typing
    this.client.on(RoomMemberEvent.Typing, wrappedCallback);

    // Return cleanup function
    return () => {
      this.client.off(RoomMemberEvent.Typing, wrappedCallback);
    };
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    switch (event) {
      case 'roomsUpdated':
        this.roomListeners.push(callback);
        break;
      case 'syncStateChanged':
        this.syncListeners.push(callback);
        break;
      case 'messageReceived':
        this.messageListeners.push(callback);
        break;
      default:
        logger.warn(`Unknown event type: ${event}`);
    }
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    switch (event) {
      case 'roomsUpdated':
        this.roomListeners = this.roomListeners.filter(listener => listener !== callback);
        break;
      case 'syncStateChanged':
        this.syncListeners = this.syncListeners.filter(listener => listener !== callback);
        break;
      case 'messageReceived':
        this.messageListeners = this.messageListeners.filter(listener => listener !== callback);
        break;
      default:
        logger.warn(`Unknown event type: ${event}`);
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    this.syncListeners = [];
    this.roomListeners = [];
    this.messageListeners = [];
  }

  /**
   * Recovery-related wrapper methods for key backup, SSSS, and cross-signing
   * These methods wrap the matrix-js-sdk APIs and handle errors/logging appropriately
   */

  /**
   * Get the current key backup version from the server
   * @returns {Promise<Object|null>} Backup version info or null if none exists
   */
  async getBackupVersion() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.warn('Crypto not available');
        return null;
      }

      const backupInfo = await crypto.getKeyBackupInfo();
      logger.debug('Got backup version:', backupInfo?.version);
      return backupInfo;
    } catch (error) {
      logger.error('Error getting backup version:', error.message);
      throw error;
    }
  }

  /**
   * Check if key backup is enabled and active
   * @returns {Promise<boolean>} True if backup is enabled
   */
  async isKeyBackupEnabled() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.warn('Crypto not available');
        return false;
      }

      const activeVersion = await crypto.getActiveSessionBackupVersion();
      const isEnabled = activeVersion !== null && activeVersion !== undefined;
      logger.debug('Key backup enabled:', isEnabled, 'version:', activeVersion);
      return isEnabled;
    } catch (error) {
      logger.error('Error checking if backup is enabled:', error.message);
      return false;
    }
  }

  /**
   * Restore key backup using a recovery key
   * @param {string} recoveryKey - The recovery key
   * @param {string} version - The backup version to restore
   * @param {Object} options - Options including progressCallback and signal
   * @returns {Promise<void>}
   */
  async restoreWithRecoveryKey(recoveryKey, version, options = {}) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    const { progressCallback, signal } = options;

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        throw new Error('Crypto not available');
      }

      // Prefer SSSS: fetch the megolm backup private key from secret storage
      // using the provided recovery key (our cryptoCallbacks supply it).
      let backupKeyBytes = null;
      try {
        const sss = this.client.getSecretStorage?.();
        const backupKeyB64 = await sss?.get?.('m.megolm_backup.v1');
        if (backupKeyB64) {
          const norm = (s) => (s || '').replace(/-/g, '+').replace(/_/g, '/');
          const withPad = (s) => s + '='.repeat((4 - (s.length % 4)) % 4);
          const b64 = withPad(norm(backupKeyB64));
          backupKeyBytes = (typeof atob === 'function')
            ? Uint8Array.from(atob(b64), c => c.charCodeAt(0))
            : Uint8Array.from(Buffer.from(b64, 'base64'));
        }
      } catch (e) {
        logger.warn('TI-M: Could not read m.megolm_backup.v1 from SSSS:', e.message);
      }

      if (!backupKeyBytes) {
        // Fallback for legacy setups: user-provided recoveryKey may actually be
        // the backup private key. Decode and use it directly.
        const { decodeRecoveryKey } = await import('matrix-js-sdk/lib/crypto-api');
        backupKeyBytes = decodeRecoveryKey(recoveryKey);
      }

      // Store the backup private key for the specific version
      await crypto.storeSessionBackupPrivateKey(backupKeyBytes, version);

      // Check abort signal
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Restore the backup
      logger.debug('Starting backup restoration for version:', version);

      // Use the crypto API to restore
      // Note: The new Rust crypto doesn't expose progressCallback directly
      // We'll need to handle progress differently
      await crypto.restoreKeyBackup(progressCallback);

      logger.debug('Backup restoration completed');
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.debug('Backup restoration aborted');
        throw error;
      }
      // Sanitize error to avoid logging recovery key
      logger.error('Error restoring backup:', error.message);
      throw error;
    }
  }

  /**
   * Get the default SSSS key ID
   * @returns {Promise<string|null>} The default key ID or null
   */
  async getSSSSDefaultKey() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const defaultKeyData = this.client.getAccountData('m.secret_storage.default_key');
      const defaultKeyId = defaultKeyData?.getContent()?.key;
      logger.debug('SSSS default key ID:', defaultKeyId);
      return defaultKeyId || null;
    } catch (error) {
      logger.error('Error getting SSSS default key:', error.message);
      return null;
    }
  }

  /**
   * Store a secret in secret storage
   * @param {string} name - Secret name
   * @param {string} data - Secret data
   * @param {string} keyId - Key ID to use for encryption
   * @returns {Promise<void>}
   */
  async putSecret(name, data, keyId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const secretStorage = this.client.getSecretStorage();
      if (!secretStorage) {
        throw new Error('Secret storage not available');
      }

      await secretStorage.store(name, data, keyId);
      logger.debug('Stored secret:', name);
    } catch (error) {
      logger.error('Error storing secret:', error.message);
      throw error;
    }
  }

  /**
   * Delete a device (sign out a specific session)
   * Uses Matrix User-Interactive Authentication (UIA) flow
   * HTTP cookies handle UIA session persistence automatically
   * @param {string} deviceId - Device ID to delete
   * @param {string} password - User's password (only needed if prompted)
   * @returns {Promise<Object>} Result with success status
   */
  async deleteDevice(deviceId, password = null) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      // First attempt: Try without auth (will use HTTP cookie if valid)
      await this.client.deleteDevice(deviceId);
      
      logger.info('Device deleted successfully (existing UIA session)');
      return { success: true, message: 'Device signed out successfully' };

    } catch (error) {
      // Check if this is a UIA challenge (401 with flows)
      if (error.httpStatus === 401 && error.data?.flows) {
        logger.debug('UIA challenge received, password required');

        // Signal UI to prompt for password if not provided
        if (!password) {
          return {
            success: false,
            needsPassword: true,
            session: error.data?.session || null,  // Pass to UI for next attempt
            flows: error.data?.flows || [],
            message: 'Password required to sign out device',
          };
        }

        // Retry with password authentication
        try {
          const userId = this.client.getUserId();
          const authData = {
            type: 'm.login.password',
            session: error.data.session,  // From 401 error
            identifier: {
              type: 'm.id.user',
              user: userId,
            },
            password: password,
          };

          await this.client.deleteDevice(deviceId, authData);
          
          logger.info('Device deleted successfully with password');
          // HTTP cookie is now set by server, valid for ~5 minutes
          return { success: true, message: 'Device signed out successfully' };

        } catch (authError) {
          logger.error('Password authentication failed:', authError.errcode);
          
          // Handle specific auth errors
          if (authError.errcode === 'M_FORBIDDEN') {
            return {
              success: false,
              message: 'Incorrect password',
              error: authError,
            };
          }
          
          throw authError;  // Re-throw unexpected errors
        }
      }

      // Not a UIA challenge - handle other errors
      logger.error('Device deletion failed:', error.message, error.errcode);
      
      let errorMessage = 'Failed to sign out device';
      if (error.errcode === 'M_NOT_FOUND') {
        errorMessage = 'Device not found';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        error: error,
      };
    }
  }

  /**
   * Setup event handler for session invalidation detection
   * Automatically logs out when token is invalidated by another session
   */
  setupSessionInvalidationHandler() {
    if (!this.client) return;

    logger.debug('Setting up session invalidation detection');

    // Listen for sync errors indicating token invalidation
    this.client.on(ClientEvent.Sync, (state, prevState, data) => {
      if (state === 'ERROR' && data?.error) {
        const error = data.error;

        // Check for token invalidation errors
        // IMPORTANT: Distinguish between UIA challenges (401 + flows) and true session invalidation
        if (error.errcode === 'M_UNKNOWN_TOKEN' ||
            (error.httpStatus === 401 && !error.data?.flows)) {
          logger.warn('Session token invalidated, forcing logout:', error.errcode);
          this.handleSessionInvalidated();
        }
      }
    });
  }

  /**
   * Handle session invalidation (token revoked, logged out elsewhere, etc.)
   */
  async handleSessionInvalidated() {
    logger.info('Session invalidated - performing automatic logout');

    // Prevent duplicate logout calls
    if (this._loggingOut) {
      logger.debug('Logout already in progress, skipping');
      return;
    }

    this._loggingOut = true;

    try {
      // Trigger logout in the app (via auth service)
      // Import dynamically to avoid circular dependencies
      const { default: matrixAuthService } = await import('./matrixAuthService');
      await matrixAuthService.logout();

      // Show notification to user
      // This will trigger app-level redirect to login page
      window.dispatchEvent(new CustomEvent('matrix:session-invalidated', {
        detail: { reason: 'Token invalidated by another session' },
      }));

    } catch (error) {
      logger.error('Error during automatic logout:', error);
    } finally {
      this._loggingOut = false;
    }
  }

  /**
   * Setup listener for incoming verification requests
   * Delegates to verificationRequestManager for smart toast notifications
   */
  setupVerificationListener() {
    if (!this.client) return;

    logger.debug('Setting up verification request listener');

    // Listen for incoming verification requests
    this.client.on(CryptoEvent.VerificationRequestReceived, async (request) => {
      logger.info('Incoming device verification request:', {
        userId: request.otherUserId || request.userId,
        deviceId: request.otherDeviceId || request.deviceId,
      });

      // Import verification request manager dynamically to avoid circular deps
      try {
        const { default: verificationRequestManager } = await import('./verificationRequestManager');
        await verificationRequestManager.handleIncomingRequest(request);
      } catch (error) {
        logger.error('MatrixService: Error handling verification request:', error);
      }
    });

    logger.info('Verification request listener active');
  }

  /**
   * Get cross-signing status
   * @returns {Promise<Object>} Cross-signing status
   */
  async getCrossSigningStatus() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        return { ready: false, publicKeysOnDevice: false };
      }

      // Check various methods depending on SDK version
      let status = { ready: false, publicKeysOnDevice: false };

      if (crypto.getCrossSigningStatus) {
        status = await crypto.getCrossSigningStatus();
      } else if (crypto.isCrossSigningReady) {
        status.ready = await crypto.isCrossSigningReady();
        status.publicKeysOnDevice = status.ready;
      } else if (this.client.getCrossSigningId) {
        const id = this.client.getCrossSigningId();
        status.ready = !!id;
        status.publicKeysOnDevice = !!id;
      }

      logger.debug('Cross-signing status:', status);
      return status;
    } catch (error) {
      logger.error('Error getting cross-signing status:', error.message);
      return { ready: false, publicKeysOnDevice: false };
    }
  }

}

// Create singleton instance
const matrixService = new MatrixService();

export default matrixService;
